import type { ApiLedgerEntry, ApiCustomerLedgerEntry, ApiSupplierLedgerEntry, ApiNotification } from "@/lib/types"

// ---- FE Interfaces (matching existing component types) ----

export interface LedgerEntry {
  id: string
  date: string
  voucherNo: string
  account: string
  description: string
  department: string
  debit: number | null
  credit: number | null
  balance: number
  isCarryover?: boolean
  isTotal?: boolean
}

export interface LedgerItem {
  id: string
  date: string
  voucherNo: string
  category: string
  product: string
  quantity: number | null
  unit: string
  unitPrice: number | null
  salesAmount: number | null
  taxAmount: number | null
  paymentAmount: number | null
  adjustmentAmount: number | null
  balance: number | null
  isSubtotal?: boolean
  isCarryover?: boolean
}

export interface SupplierLedgerItem {
  id: string
  date: string
  voucherNo: string
  category: string
  product: string
  quantity: number | null
  unit: string
  unitPrice: number | null
  purchaseAmount: number | null
  taxAmount: number | null
  paymentAmount: number | null
  adjustmentAmount: number | null
  balance: number | null
  isSubtotal?: boolean
  isCarryover?: boolean
}

export interface Notice {
  id: number
  type: "maintenance" | "update" | "info" | "sync_success" | "sync_error" | "token_expired" | "quota_warning"
  title: string
  description: string
  date: string
  isNew: boolean
}

// ---- Transforms ----

export function transformLedgerEntries(apiEntries: ApiLedgerEntry[]): LedgerEntry[] {
  const entries: LedgerEntry[] = apiEntries.map((e) => ({
    id: e.entry_id,
    date: formatDateSlash(e.entry_date),
    voucherNo: e.voucher_no,
    account: e.account_name,
    description: e.description,
    department: e.department_name,
    debit: Number(e.debit_amount) > 0 ? Number(e.debit_amount) : null,
    credit: Number(e.credit_amount) > 0 ? Number(e.credit_amount) : null,
    balance: Number(e.balance),
    isCarryover: e.is_carryover === 1,
  }))

  // Append total row (matches existing FE pattern)
  if (entries.length > 0) {
    const nonCarryover = entries.filter((e) => !e.isCarryover)
    const totalDebit = nonCarryover.reduce((sum, e) => sum + (e.debit || 0), 0)
    const totalCredit = nonCarryover.reduce((sum, e) => sum + (e.credit || 0), 0)
    const lastBalance = entries[entries.length - 1].balance

    entries.push({
      id: "total",
      date: "",
      voucherNo: "",
      account: "",
      description: "合計",
      department: "",
      debit: totalDebit > 0 ? totalDebit : null,
      credit: totalCredit > 0 ? totalCredit : null,
      balance: lastBalance,
      isTotal: true,
    })
  }

  return entries
}

export function transformCustomerLedgerEntries(apiEntries: ApiCustomerLedgerEntry[]): LedgerItem[] {
  const items: LedgerItem[] = []

  // Group entries by voucher_no to insert subtotals
  let currentVoucher = ""
  let groupSales = 0
  let groupTax = 0
  let groupPayment = 0
  let groupAdjustment = 0
  let groupIdx = 0

  for (const e of apiEntries) {
    const isCarryover = e.is_carryover === 1

    // If voucher changed and we have a previous group, insert subtotal
    if (!isCarryover && currentVoucher && e.voucher_no !== currentVoucher) {
      items.push({
        id: `sub-${groupIdx}`,
        date: "",
        voucherNo: "",
        category: "",
        product: "伝票計",
        quantity: null,
        unit: "",
        unitPrice: null,
        salesAmount: groupSales || null,
        taxAmount: groupTax || null,
        paymentAmount: groupPayment || null,
        adjustmentAmount: groupAdjustment || null,
        balance: null,
        isSubtotal: true,
      })
      groupSales = 0
      groupTax = 0
      groupPayment = 0
      groupAdjustment = 0
      groupIdx++
    }

    items.push({
      id: e.ledger_id,
      date: isCarryover ? "" : formatDateJapanese(e.transaction_date),
      voucherNo: e.voucher_no,
      category: e.category,
      product: isCarryover ? "繰越残高" : e.product_name,
      quantity: Number(e.quantity) > 0 ? Number(e.quantity) : null,
      unit: e.unit || "",
      unitPrice: Number(e.unit_price) > 0 ? Number(e.unit_price) : null,
      salesAmount: Number(e.sales_amount) !== 0 ? Number(e.sales_amount) : null,
      taxAmount: Number(e.tax_amount) !== 0 ? Number(e.tax_amount) : null,
      paymentAmount: Number(e.payment_amount) !== 0 ? Number(e.payment_amount) : null,
      adjustmentAmount: Number(e.adjustment_amount) !== 0 ? Number(e.adjustment_amount) : null,
      balance: Number(e.balance),
      isCarryover,
    })

    if (!isCarryover) {
      currentVoucher = e.voucher_no
      groupSales += Number(e.sales_amount)
      groupTax += Number(e.tax_amount)
      groupPayment += Number(e.payment_amount)
      groupAdjustment += Number(e.adjustment_amount)
    }
  }

  // Final subtotal for last group
  if (currentVoucher) {
    items.push({
      id: `sub-${groupIdx}`,
      date: "",
      voucherNo: "",
      category: "",
      product: "伝票計",
      quantity: null,
      unit: "",
      unitPrice: null,
      salesAmount: groupSales || null,
      taxAmount: groupTax || null,
      paymentAmount: groupPayment || null,
      adjustmentAmount: groupAdjustment || null,
      balance: null,
      isSubtotal: true,
    })
  }

  return items
}

export function transformSupplierLedgerEntries(apiEntries: ApiSupplierLedgerEntry[]): SupplierLedgerItem[] {
  const items: SupplierLedgerItem[] = []

  let currentVoucher = ""
  let groupPurchase = 0
  let groupTax = 0
  let groupPayment = 0
  let groupAdjustment = 0
  let groupIdx = 0

  for (const e of apiEntries) {
    const isCarryover = e.is_carryover === 1

    if (!isCarryover && currentVoucher && e.voucher_no !== currentVoucher) {
      items.push({
        id: `sub-${groupIdx}`,
        date: "",
        voucherNo: "",
        category: "",
        product: "伝票計",
        quantity: null,
        unit: "",
        unitPrice: null,
        purchaseAmount: groupPurchase || null,
        taxAmount: groupTax || null,
        paymentAmount: groupPayment || null,
        adjustmentAmount: groupAdjustment || null,
        balance: null,
        isSubtotal: true,
      })
      groupPurchase = 0
      groupTax = 0
      groupPayment = 0
      groupAdjustment = 0
      groupIdx++
    }

    items.push({
      id: e.ledger_id,
      date: isCarryover ? "" : formatDateJapanese(e.transaction_date),
      voucherNo: e.voucher_no,
      category: e.category,
      product: isCarryover ? "繰越残高" : e.product_name,
      quantity: Number(e.quantity) > 0 ? Number(e.quantity) : null,
      unit: e.unit || "",
      unitPrice: Number(e.unit_price) > 0 ? Number(e.unit_price) : null,
      purchaseAmount: Number(e.purchase_amount) !== 0 ? Number(e.purchase_amount) : null,
      taxAmount: Number(e.tax_amount) !== 0 ? Number(e.tax_amount) : null,
      paymentAmount: Number(e.payment_amount) !== 0 ? Number(e.payment_amount) : null,
      adjustmentAmount: Number(e.adjustment_amount) !== 0 ? Number(e.adjustment_amount) : null,
      balance: Number(e.balance),
      isCarryover,
    })

    if (!isCarryover) {
      currentVoucher = e.voucher_no
      groupPurchase += Number(e.purchase_amount)
      groupTax += Number(e.tax_amount)
      groupPayment += Number(e.payment_amount)
      groupAdjustment += Number(e.adjustment_amount)
    }
  }

  if (currentVoucher) {
    items.push({
      id: `sub-${groupIdx}`,
      date: "",
      voucherNo: "",
      category: "",
      product: "伝票計",
      quantity: null,
      unit: "",
      unitPrice: null,
      purchaseAmount: groupPurchase || null,
      taxAmount: groupTax || null,
      paymentAmount: groupPayment || null,
      adjustmentAmount: groupAdjustment || null,
      balance: null,
      isSubtotal: true,
    })
  }

  return items
}

export function transformNotifications(apiNotifications: ApiNotification[]): Notice[] {
  const validTypes = new Set([
    "maintenance", "update", "info", "sync_success", "sync_error", "token_expired", "quota_warning",
  ])

  return apiNotifications.map((n) => ({
    id: n.id,
    type: (validTypes.has(n.type) ? n.type : "info") as Notice["type"],
    title: n.title,
    description: n.message,
    date: formatDateSlash(n.created_at.split("T")[0]),
    isNew: !n.is_read,
  }))
}

// ---- Date helpers ----

function formatDateSlash(isoDate: string): string {
  return isoDate.replace(/-/g, "/")
}

function formatDateJapanese(isoDate: string): string {
  const [y, m, d] = isoDate.split("-")
  return `${y}年 ${parseInt(m)}月 ${parseInt(d)}日`
}
